import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HorizontalBarChartWithAxis } from '@fluentui/react-charting';
import type { IHorizontalBarChartWithAxisDataPoint } from '@fluentui/react-charting';
import { tokens } from '@fluentui/react-components';
import { WidgetContainer } from '../WidgetContainer';
import { ChartThemeProvider } from '../ChartThemeProvider';
import type { RoomDetail, RoomTypeConfig } from '@/types/reports';
import { useRoomTypeBarChartStyles } from './RoomTypeBarChart.styles';

export interface RoomTypeBarChartProps {
  data: RoomDetail[];
  roomTypes: RoomTypeConfig[];
  mode: 'utilization' | 'bookings';
  titleKey: string;
}

export const RoomTypeBarChart = ({ data, roomTypes, mode, titleKey }: RoomTypeBarChartProps) => {
  const styles = useRoomTypeBarChartStyles();
  const { t } = useTranslation('reports');

  const chartData: IHorizontalBarChartWithAxisDataPoint[] = useMemo(() => {
    const colorMap = new Map(roomTypes.map((rt) => [rt.name, rt.color]));
    const groups = new Map<string, { totalUtil: number; totalBookings: number; count: number }>();

    for (const room of data) {
      const g = groups.get(room.roomType) ?? { totalUtil: 0, totalBookings: 0, count: 0 };
      g.totalUtil += room.utilization;
      g.totalBookings += room.bookings;
      g.count++;
      groups.set(room.roomType, g);
    }

    const points: IHorizontalBarChartWithAxisDataPoint[] = [];
    for (const [type, g] of groups) {
      points.push({
        x: mode === 'utilization' ? Math.round(g.totalUtil / g.count) : g.totalBookings,
        y: type,
        color: colorMap.get(type) ?? tokens.colorNeutralForeground3,
      });
    }

    return points.sort((a, b) => b.x - a.x);
  }, [data, roomTypes, mode]);

  return (
    <WidgetContainer title={t(titleKey)}>
      <div className={styles.root} role="figure" aria-label={t(titleKey)}>
        <ChartThemeProvider>
          <HorizontalBarChartWithAxis
            data={chartData}
            showYAxisLables
            height={300}
            enableReflow={true}
            hideLegend
          />
        </ChartThemeProvider>
      </div>
    </WidgetContainer>
  );
};

RoomTypeBarChart.displayName = 'RoomTypeBarChart';
